import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { cachedGet } from '@/lib/apiClient';
import ModalPerfil from '@/components/auth/ModalPerfil';
import HelpMenu from '@/components/dashboard/HelpMenu';
import GuideTour, { TOUR_STORAGE_KEY } from '@/components/dashboard/GuideTour';
import {
  LayoutDashboard, Users, Briefcase, CalendarDays, Receipt,
  FolderLock, FileText, BarChart3, LogOut, Menu, X, Scale,
  MessageSquare, Sparkles, ShieldCheck, Bell, AlertTriangle,
  Clock, CalendarClock, ChevronRight, FolderOpen, CreditCard,
  ChevronLeft, Import, FileSearch, Landmark
} from 'lucide-react';

const LOGO = "/logo.png";

/* ─── Saludo inteligente ──────────────────────────────────────── */
function useSaludo(nombre) {
  const hora = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires' })
  ).getHours();

  const turno = hora >= 6 && hora < 12 ? 'Buenos días'
              : hora >= 12 && hora < 20 ? 'Buenas tardes'
              : 'Buenas noches';

  // Detecta género por terminación del primer nombre
  const primerNombre = (nombre || '').trim().split(' ')[0].toLowerCase();
  const masculinosEn_a = ['luca', 'nikita', 'andrea', 'ilia', 'illia'];
  const femeninos_excep = ['sol', 'luz', 'mar', 'paz', 'flor', 'eden', 'noel'];
  let esFemenino = false;
  if (femeninos_excep.includes(primerNombre)) {
    esFemenino = true;
  } else if (!masculinosEn_a.includes(primerNombre)) {
    esFemenino = primerNombre.endsWith('a') || primerNombre.endsWith('ina') || primerNombre.endsWith('ela');
  }
  const titulo = esFemenino ? 'Dra.' : 'Dr.';
  const apellido = (nombre || '').trim().split(' ').slice(-1)[0];

  return `${turno}, ${titulo} ${apellido}`;
}

/* ─── Nav con categorías ──────────────────────────────────────── */
const NAV_GROUPS = [
  {
    label: 'Principal',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Panel' },
    ],
  },
  {
    label: 'Gestión',
    items: [
      { to: '/clientes',    icon: Users,       label: 'Clientes' },
      { to: '/expedientes', icon: Briefcase,   label: 'Expedientes' },
      { to: '/carpetas',    icon: FolderOpen,  label: 'Carpetas' },
    ],
  },
  {
    label: 'Operaciones',
    items: [
      { to: '/agenda',      icon: CalendarDays, label: 'Agenda' },
      { to: '/facturacion', icon: Receipt,      label: 'Facturación' },
      { to: '/vault',       icon: FolderLock,   label: 'Bóveda' },
      { to: '/documentos',  icon: FileText,     label: 'Documentos' },
    ],
  },
  {
    label: 'Herramientas',
    items: [
      { to: '/asistente',      icon: Sparkles,     label: 'Asistente IA' },
      { to: '/analisis-casos', icon: FileSearch,   label: 'Análisis de Casos' },
      { to: '/sincronizacion-judicial', icon: Landmark, label: 'Sincronización Judicial' },
      { to: '/chat',           icon: MessageSquare,label: 'Chat interno', teamOnly: true },
      { to: '/reportes',       icon: BarChart3,    label: 'Reportes' },
    ],
  },
  {
    label: 'Cuenta',
    items: [
      { to: '/suscripcion',  icon: CreditCard, label: 'Mi suscripción' },
      { to: '/importacion',  icon: FileText,   label: 'Importar datos', adminOnly: true },
      { to: '/administracion',icon: ShieldCheck,label: 'Administración', adminOnly: true },
    ],
  },
];

const fmtM = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtF = (s) => { try { return new Date(s).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }); } catch { return s || ''; } };
const fmtH = (s) => { try { return new Date(s).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

/* ─── Panel de notificaciones ─────────────────────────────────── */
function PanelNotificaciones({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/notificaciones/pendientes')
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const ir = (path) => { navigate(path); onClose(); };

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-xl shadow-2xl z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <span className="text-sm font-medium">Notificaciones</span>
        <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-4 h-4" /></button>
      </div>
      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-5 h-5 border-2 border-[#C9A44C] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !data || data.total === 0 ? (
        <div className="py-8 text-center text-[#666] text-sm">Sin notificaciones</div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          {data.vencidos?.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-red-500/5 border-b border-red-500/10 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-[10px] uppercase tracking-wide text-red-400">Honorarios vencidos</span>
              </div>
              {data.vencidos.slice(0, 3).map((h) => (
                <button key={h.id} onClick={() => ir('/facturacion')}
                  className="w-full text-left flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.04] border-b border-white/[0.03] transition-all">
                  <div className="min-w-0">
                    <div className="text-xs truncate">{h.cliente}</div>
                    <div className="text-[10px] text-red-400">{fmtF(h.fecha_vencimiento)}</div>
                  </div>
                  <div className="text-xs text-red-300 ml-2 flex-shrink-0">{fmtM((h.monto || 0) - (h.monto_pagado || 0))}</div>
                </button>
              ))}
            </div>
          )}
          {data.por_vencer?.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-[#C9A44C]/5 border-b border-[#C9A44C]/10 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#C9A44C]" />
                <span className="text-[10px] uppercase tracking-wide text-[#C9A44C]">Por vencer (7 días)</span>
              </div>
              {data.por_vencer.slice(0, 3).map((h) => (
                <button key={h.id} onClick={() => ir('/facturacion')}
                  className="w-full text-left flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.04] border-b border-white/[0.03] transition-all">
                  <div className="min-w-0">
                    <div className="text-xs truncate">{h.cliente}</div>
                    <div className="text-[10px] text-[#888]">{fmtF(h.fecha_vencimiento)}</div>
                  </div>
                  <div className="text-xs text-[#C9A44C] ml-2 flex-shrink-0">{fmtM((h.monto || 0) - (h.monto_pagado || 0))}</div>
                </button>
              ))}
            </div>
          )}
          {data.eventos_proximos?.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-blue-500/5 border-b border-blue-500/10 flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] uppercase tracking-wide text-blue-400">Eventos próximos (48h)</span>
              </div>
              {data.eventos_proximos.slice(0, 3).map((ev) => (
                <button key={ev.id} onClick={() => ir('/agenda')}
                  className="w-full text-left flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.04] border-b border-white/[0.03] transition-all">
                  <div className="min-w-0">
                    <div className="text-xs truncate">{ev.titulo}</div>
                    <div className="text-[10px] text-[#888]">{fmtH(ev.inicio)}</div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-[#888] flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Campana ─────────────────────────────────────────────────── */
function BellBtn() {
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const poll = async () => {
      try { const r = await api.get('/api/notificaciones/pendientes'); setTotal(r.data?.total || 0); } catch {}
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative" data-tour="notificaciones">
      <button onClick={() => setOpen(!open)} className="relative p-2 text-[#888] hover:text-[#F8F6F0] transition-colors">
        <Bell className="w-5 h-5" />
        {total > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center px-1 font-bold">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      {open && <PanelNotificaciones onClose={() => setOpen(false)} />}
    </div>
  );
}

/* ─── Trial banner ────────────────────────────────────────────── */
function TrialBanner({ info }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (!info || info.estado !== 'trial' || (info.dias_trial_restantes ?? 99) > 5) return null;
  const dias = info.dias_trial_restantes ?? 0;
  const color = dias <= 1 ? 'bg-red-500/15 border-red-500/30 text-red-300' : 'bg-[#C9A44C]/10 border-[#C9A44C]/25 text-[#C9A44C]';
  // Estando ya en /suscripcion, re-navegar no hacía nada: ahora baja al botón
  // de pago. Desde cualquier otra página, navega a la de suscripción.
  const irActivar = () => {
    if (location.pathname === '/suscripcion') {
      document.getElementById('activar-suscripcion')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      navigate('/suscripcion');
    }
  };
  return (
    <div className={`mx-5 mt-3 flex items-center justify-between px-4 py-2.5 rounded-xl border text-xs ${color}`}>
      <span>{dias === 0 ? '⚠️ Tu período de prueba venció hoy' : `⏱ Te quedan ${dias} día${dias !== 1 ? 's' : ''} de prueba gratuita`}</span>
      <button onClick={irActivar} className="px-3 py-1 rounded-lg bg-[#C9A44C] text-[#070707] font-semibold text-[11px]">
        Contratar plan
      </button>
    </div>
  );
}

/* ─── Tooltip para modo colapsado ─────────────────────────────── */
function NavTooltip({ label, children }) {
  return (
    <div className="relative group/tt">
      {children}
      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-[#1A1A1A] border border-[rgba(201,164,76,0.2)] text-[#F8F6F0] text-xs rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover/tt:opacity-100 transition-opacity z-50 shadow-lg">
        {label}
        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#1A1A1A]" />
      </div>
    </div>
  );
}

/* ─── Layout principal ────────────────────────────────────────── */
export default function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const [showPerfil, setShowPerfil] = useState(false);
  const [suscripcionInfo, setSuscripcionInfo] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const collapsedBeforeTour = useRef(false);

  useEffect(() => {
    cachedGet('/api/suscripcion/estado', 120_000)
      .then((r) => setSuscripcionInfo(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      if (!localStorage.getItem(TOUR_STORAGE_KEY)) setShowTour(true);
    } catch {}
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem('sidebar_collapsed', String(next)); } catch {}
  };

  const handleTourForceExpand = (force) => {
    if (force) {
      collapsedBeforeTour.current = collapsed;
      if (collapsed) setCollapsed(false);
    } else if (collapsedBeforeTour.current) {
      setCollapsed(true);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };
  const inicial = (user?.nombre || 'U').charAt(0).toUpperCase();
  const esAdmin = ['admin', 'Socio Director'].includes(user?.rol);
  const saludo = useSaludo(user?.nombre);
  const esIndependiente = suscripcionInfo?.plan === 'independiente';

  const sidebarW = collapsed ? 'w-[68px]' : 'w-60';
  const mainPad  = collapsed ? 'lg:pl-[68px]' : 'lg:pl-60';

  return (
    <div className="min-h-screen bg-[#070707] text-[#F8F6F0]" style={{ fontFamily: 'Outfit, sans-serif' }}>

      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside className={`
        fixed inset-y-0 left-0 z-40 ${sidebarW}
        bg-[#0A0A0A] border-r border-[rgba(201,164,76,0.12)]
        transform transition-all duration-300 ease-in-out
        lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col overflow-hidden
      `}>

        {/* Logo */}
        <div className={`flex items-center justify-center border-b border-[rgba(201,164,76,0.12)] flex-shrink-0 transition-all duration-300 ${collapsed ? 'h-16 px-2' : 'h-24 px-4'}`}>
          {collapsed ? (
            <img src={LOGO} alt="LexOrbit" className="w-10 h-10 object-contain" />
          ) : (
            <img src={LOGO} alt="LexOrbit" className="w-full max-w-[168px] h-auto object-contain" />
          )}
        </div>

        {/* Nav con categorías */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 space-y-0.5 scrollbar-hide">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((n) => {
              if (n.adminOnly && !esAdmin) return false;
              if (n.teamOnly && esIndependiente) return false;
              return true;
            });
            if (items.length === 0) return null;

            return (
              <div key={group.label} className="mb-1">
                {/* Etiqueta de categoría */}
                {!collapsed && (
                  <div className="px-4 pt-3 pb-1">
                    <span className="text-[9px] uppercase tracking-[0.18em] text-[#444] font-semibold">
                      {group.label}
                    </span>
                  </div>
                )}
                {collapsed && (
                  <div className="h-px mx-3 bg-white/[0.06] my-2" />
                )}

                {items.map(({ to, icon: Icon, label }) =>
                  collapsed ? (
                    <NavTooltip key={to} label={label}>
                      <NavLink to={to} data-tour={`nav-${to.slice(1)}`} onClick={() => setMobileOpen(false)} end={to === '/dashboard'}
                        className={({ isActive }) =>
                          `flex items-center justify-center w-full h-10 rounded-lg mx-auto transition-all duration-200 ${
                            isActive
                              ? 'text-[#C9A44C] bg-[#C9A44C]/12'
                              : 'text-[#777] hover:text-[#F8F6F0] hover:bg-white/[0.05]'
                          }`
                        }>
                        <Icon className="w-[18px] h-[18px]" />
                      </NavLink>
                    </NavTooltip>
                  ) : (
                    <NavLink key={to} to={to} data-tour={`nav-${to.slice(1)}`} onClick={() => setMobileOpen(false)} end={to === '/dashboard'}
                      className={({ isActive }) =>
                        `flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                          isActive
                            ? 'bg-[#C9A44C]/12 text-[#C9A44C] border border-[#C9A44C]/20'
                            : 'text-[#A0A0A0] hover:text-[#F8F6F0] hover:bg-white/[0.04] border border-transparent'
                        }`
                      }>
                      <Icon className="w-[17px] h-[17px] flex-shrink-0" />
                      <span className="truncate">{label}</span>
                    </NavLink>
                  )
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer sidebar: cerrar sesión + toggle */}
        <div className="border-t border-[rgba(201,164,76,0.12)] flex-shrink-0 p-2 space-y-1">
          {collapsed ? (
            <NavTooltip label="Cerrar sesión">
              <button onClick={handleLogout}
                className="flex items-center justify-center w-full h-10 rounded-lg text-[#777] hover:text-red-400 hover:bg-red-500/10 transition-all">
                <LogOut className="w-[18px] h-[18px]" />
              </button>
            </NavTooltip>
          ) : (
            <button onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#A0A0A0] hover:text-red-400 hover:bg-red-500/10 transition-all w-full">
              <LogOut className="w-[17px] h-[17px]" /> Cerrar sesión
            </button>
          )}

          {/* Toggle collapse — solo desktop */}
          <button onClick={toggleCollapsed}
            className={`hidden lg:flex items-center rounded-lg text-[#555] hover:text-[#C9A44C] hover:bg-[#C9A44C]/08 transition-all w-full py-2 ${collapsed ? 'justify-center' : 'gap-3 px-3'}`}>
            {collapsed
              ? <ChevronRight className="w-4 h-4" />
              : <><ChevronLeft className="w-4 h-4" /><span className="text-xs">Contraer menú</span></>
            }
          </button>
        </div>
      </aside>

      {/* Overlay mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Main ────────────────────────────────────────────────── */}
      <div className={`${mainPad} transition-all duration-300`}>
        <header className="h-[72px] sticky top-0 z-20 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-[rgba(201,164,76,0.12)] flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            {/* Hamburger mobile */}
            <button className="lg:hidden text-[#888] hover:text-[#F8F6F0]" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex flex-col leading-tight">
              <span className="text-[11px] text-[#C9A44C] tracking-wide">{saludo}</span>
              <h1 className="text-lg tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                {title || 'Panel de Control'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <HelpMenu onStartTour={() => setShowTour(true)} />
            <BellBtn />
            <div className="w-px h-5 bg-white/10" />
            <div className="text-right hidden sm:block">
              <div className="text-sm text-[#F8F6F0] leading-tight">{user?.nombre || 'Usuario'}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#C9A44C]">{user?.rol || ''}</div>
            </div>
            <button onClick={() => setShowPerfil(true)}
              className="w-9 h-9 rounded-full bg-[#C9A44C]/15 border border-[#C9A44C]/30 flex items-center justify-center text-[#C9A44C] text-sm font-medium hover:bg-[#C9A44C]/25 transition-all overflow-hidden">
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                : inicial}
            </button>
          </div>
        </header>

        <TrialBanner info={suscripcionInfo} />
        <main className="p-5 sm:p-7 max-w-[1400px] mx-auto">{children}</main>
      </div>

      {showPerfil && <ModalPerfil onClose={() => setShowPerfil(false)} />}
      {showTour && <GuideTour onClose={() => setShowTour(false)} forceExpand={handleTourForceExpand} />}
    </div>
  );
}

export { Scale };
